import React, { useState, useEffect, useCallback } from 'react';
import { X, FileOutput, Eye, FileText, Loader2 } from 'lucide-react';
import { documentTemplateService, caseService } from '../../services/api';
import useModal from '../../hooks/useModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../common/Notification';
import SearchableSelect from '../common/SearchableSelect';

const GenerateDocumentModal = ({ template, onClose }) => {
  const [caseId, setCaseId] = useState('');
  const [outputFormat, setOutputFormat] = useState('docx');
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const { notification, showError, hideNotification } = useNotification();
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, () => onClose(false));

  useEffect(() => {
    caseService.getCases({ status: 'active', limit: 500 }).then(res => {
      const caseList = (res.data.cases || res.data || []).map(c => ({
        value: c.id,
        label: `${c.case_number} — ${c.title}`,
        sublabel: c.client_name || '',
      }));
      setCases(caseList);
    }).catch(() => {
      showError('Failed to load cases');
    }).finally(() => setLoadingCases(false));
  }, [showError]);

  const handlePreview = useCallback(async () => {
    if (!caseId) {
      showError('Please select a case first');
      return;
    }
    try {
      setPreviewing(true);
      setPreviewContent(null);
      const res = await documentTemplateService.preview(template.id, {
        case_id: caseId,
        output_format: outputFormat,
      });
      setPreviewContent(res.data.filled_content);
    } catch {
      showError('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  }, [caseId, outputFormat, template.id, showError]);

  const handleGenerate = async () => {
    if (!caseId) {
      showError('Please select a case');
      return;
    }
    try {
      setGenerating(true);
      await documentTemplateService.generate(template.id, {
        case_id: caseId,
        output_format: outputFormat,
      });
      onClose(true);
    } catch {
      showError('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <Notification {...notification} onClose={hideNotification} />
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileOutput className="h-5 w-5 text-green-600" />
              Generate Document
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              From template: <span className="font-medium">{template.name}</span>
            </p>
          </div>
          <button onClick={() => onClose(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Case selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Case *</label>
            <p className="text-xs text-gray-400 mb-2">
              The template will auto-fill with data from the selected case, its client, and assigned attorney.
            </p>
            {loadingCases ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading cases...
              </div>
            ) : (
              <SearchableSelect
                value={caseId}
                onChange={setCaseId}
                options={cases}
                placeholder="Select a case..."
                searchPlaceholder="Search by case number or title..."
              />
            )}
          </div>

          {/* Output format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Output Format</label>
            <div className="flex gap-3">
              <label className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
                outputFormat === 'docx' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="format"
                  value="docx"
                  checked={outputFormat === 'docx'}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  className="sr-only"
                />
                <FileText className="h-4 w-4" />
                <span className="text-sm font-medium">Word (.docx)</span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
                outputFormat === 'pdf' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={outputFormat === 'pdf'}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  className="sr-only"
                />
                <FileText className="h-4 w-4" />
                <span className="text-sm font-medium">PDF (.pdf)</span>
              </label>
            </div>
          </div>

          {/* Preview section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Preview</label>
              <button
                onClick={handlePreview}
                disabled={!caseId || previewing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                {previewing ? 'Loading...' : 'Preview with Case Data'}
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 max-h-72 overflow-y-auto">
              {previewContent ? (
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-serif leading-relaxed">
                  {previewContent}
                </pre>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">
                  Select a case and click "Preview with Case Data" to see the filled template
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={!caseId || generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileOutput className="h-4 w-4" />}
            {generating ? 'Generating...' : 'Generate & Save to Case'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateDocumentModal;
